import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import {
  useGetTransferDetailsQuery,
  useUpdateTransferMutation,
  useUploadTransferImagesMutation,   // ⭐ ADD THIS
} from '../slices/transfersApiSlice';

import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const TransferEditScreen = () => {
const { id: transferId } = useParams();
const navigate = useNavigate();
const dispatch = useDispatch();

const { cartItems } = useSelector((state) => state.cart);

const [mergedOnce, setMergedOnce] = useState(false);

// Customer fields
const [customerSearch, setCustomerSearch] = useState('');
const [filteredCustomers, setFilteredCustomers] = useState([]);
const [selectedCustomer, setSelectedCustomer] = useState(null);
const [note, setNote] = useState('');
const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);

// ⭐ NEW — Images (filenames)
const [images, setImages] = useState([]);

// API hooks
const { data: transferData, isLoading: loadingTransfer, error: transferError } =
  useGetTransferDetailsQuery(transferId);

const { data: customersData, isLoading: loadingCustomers, error: customersError } =
  useGetCustomersQuery({ keyword: customerSearch });

const [updateTransfer, { isLoading: savingTransfer }] = useUpdateTransferMutation();
const [uploadTransferImages] = useUploadTransferImagesMutation();

// Normalize cart items
const normalizeCart = (items) =>
  items.map((item) => ({
    ...item,
    product: item.product || item._id,
  }));

// Merge transfer items with cart items
const mergeTransferWithCart = (transferItems, cartItems) => {
  const map = new Map();

  normalizeCart(cartItems).forEach((item) => {
    if (!item.product) return;
    map.set(item.product.toString(), { ...item });
  });

  transferItems.forEach((item) => {
    if (!item.product) return;
    map.set(item.product.toString(), { ...item });
  });

  return Array.from(map.values());
};

// Load transfer data ONCE
useEffect(() => {
  if (transferData && !mergedOnce) {
    setSelectedCustomer(transferData.customerDetails || null);
    setNote(transferData.note || '');
    setTransferDate(
      transferData.date ? transferData.date.split('T')[0] : new Date().toISOString().split('T')[0]
    );

    // ⭐ Load existing images
    setImages(transferData.images || []);

    const merged = mergeTransferWithCart(transferData.transferItems, cartItems);

    merged.forEach((item) => {
      dispatch(
        addToCart({
          ...item,
          product: item.product,
          qty: item.qty,
        })
      );
    });

    setMergedOnce(true);
  }
}, [transferData, mergedOnce]);

// Filter customers
useEffect(() => {
  if (customersData?.customers) {
    const filtered = customersData.customers.filter((customer) =>
      customer.name.toLowerCase().includes(customerSearch.toLowerCase())
    );
    setFilteredCustomers(filtered);
  }
}, [customerSearch, customersData]);

const handleCustomerSelect = (customer) => {
  setSelectedCustomer(customer);
  setCustomerSearch(customer.name);
  setFilteredCustomers([]);
};

// Qty handlers
const increaseQtyHandler = (item) => {
  dispatch(addToCart({ ...item, qty: item.qty + 1 }));
};

const decreaseQtyHandler = (item) => {
  if (item.qty > 1) {
    dispatch(addToCart({ ...item, qty: item.qty - 1 }));
  } else {
    toast.error('Quantity cannot be less than 1');
  }
};

const removeFromCartHandler = (productId) => {
  dispatch(removeFromCart(productId));
};

// ⭐ Validate files
const validateFiles = (files) => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const maxSize = 5 * 1024 * 1024;

  for (let file of files) {
    if (!allowedTypes.includes(file.type)) {
      toast.error(`File type not allowed: ${file.type}`);
      return false;
    }
    if (file.size > maxSize) {
      toast.error(`File size exceeds 5MB: ${file.name}`);
      return false;
    }
  }
  return true;
};

// ⭐ Upload images BEFORE saving transfer
const uploadImageHandler = async (files) => {
  if (!validateFiles(files)) return;

  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append('images', file));

  try {
    const res = await uploadTransferImages(formData).unwrap();
    toast.success(res.message);

    // Append new filenames
    setImages((prev) => [...prev, ...res.images]);
  } catch (err) {
    toast.error(err?.data?.message || 'Image upload failed');
  }
};

// ⭐ Remove image locally
const removeImageHandler = (filename) => {
  setImages(images.filter((img) => img !== filename));
};

// ⭐ Update transfer
const updateTransferHandler = async () => {
  try {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    const transferItems = normalizeCart(cartItems).map((item) => ({
      product: item.product,
      qty: item.qty,
    }));

    await updateTransfer({
      transferId,
      transferItems,
      customerDetails: selectedCustomer._id,
      note,
      status: 'Completed',
      date: transferDate,
      images, // ⭐ send updated images
    }).unwrap();

    toast.success('Transfer updated successfully');
    dispatch(clearCartItems());
    navigate('/admin/transferlist');
  } catch (err) {
    toast.error(err?.data?.message || 'Failed to update transfer');
  }
};

return (
  <Row>
    <Col md={8}>
      <h1>Edit Transfer</h1>

      {loadingTransfer ? (
        <Loader />
      ) : transferError ? (
        <Message variant="danger">{transferError?.data?.message}</Message>
      ) : (
        <>
          {cartItems.length === 0 ? (
            <Message>
              Your cart is empty.{' '}
              <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
            </Message>
          ) : (
            <ListGroup variant="flush">
              {normalizeCart(cartItems).map((item) => (
                <ListGroup.Item key={item.product}>
                  <Row>
                    <Col md={2}>
                      {item.images?.length > 0 && (
                        <img
                          src={item.images[0]}
                          alt="Product"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                        />
                      )}
                    </Col>

                    <Col>{item.name}</Col>

                    <Col md={2}>D{item.price.toFixed(2)}</Col>

                    <Col md={3}>
                      <div className="d-flex align-items-center">
                        <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                          <FaMinus />
                        </Button>
                        <span className="mx-2">{item.qty}</span>
                        <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                          <FaPlus />
                        </Button>
                      </div>
                    </Col>

                    <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>

                    <Col md={1}>
                      <Button variant="light" onClick={() => removeFromCartHandler(item.product)}>
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </>
      )}
    </Col>

    <Col md={4}>
      <Card>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <h2>Select Customer</h2>

            {loadingCustomers ? (
              <Loader />
            ) : customersError ? (
              <Message variant="danger">{customersError?.data?.message}</Message>
            ) : (
              <>
                <Form.Group controlId="customerSearch">
                  <Form.Label>Search Customer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type customer name"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                </Form.Group>

                {filteredCustomers.length > 0 && (
                  <ListGroup>
                    {filteredCustomers.map((customer) => (
                      <ListGroup.Item
                        key={customer._id}
                        action
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        {customer.name} ({customer.company})
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}

                {selectedCustomer && (
                  <div className="mt-3">
                    <strong>Selected Customer:</strong>
                    <p>
                      {selectedCustomer.name} ({selectedCustomer.company})<br />
                      Phone: {selectedCustomer.telephone}<br />
                      Address: {selectedCustomer.address}<br />
                      SERIAL: {selectedCustomer.serial}
                    </p>

                    <Form.Group controlId="note">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="transferDate" className="mt-2">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={transferDate}
                        onChange={(e) => setTransferDate(e.target.value)}
                      />
                    </Form.Group>

                    {/* ⭐ NEW — IMAGE UPLOAD + PREVIEW */}
                    <Form.Group controlId="images" className="mt-3">
                      <Form.Label>Upload Images</Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        onChange={(e) => uploadImageHandler(e.target.files)}
                      />

                      <div className="mt-3">
                        {images.length === 0 && (
                          <Message>No images uploaded for this transfer</Message>
                        )}

                        {images.map((img, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'inline-block',
                              position: 'relative',
                              marginRight: '10px',
                            }}
                          >
                            <img
                              src={`/uploads/${img}`}
                              alt={`Transfer Image ${idx}`}
                              className="img-fluid"
                              style={{
                                maxWidth: '100px',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                              }}
                            />

                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeImageHandler(img)}
                              style={{
                                position: 'absolute',
                                top: '0',
                                right: '0',
                                borderRadius: '50%',
                                padding: '2px 5px',
                              }}
                            >
                              ✖
                            </Button>
                          </div>
                        ))}
                      </div>
                    </Form.Group>
                  </div>
                )}
              </>
            )}
          </ListGroup.Item>

          <ListGroup.Item>
            <Button
              type="button"
              className="btn-block"
              disabled={savingTransfer || cartItems.length === 0 || !selectedCustomer}
              onClick={updateTransferHandler}
            >
              {savingTransfer ? 'Processing...' : 'Update Transfer'}
            </Button>
          </ListGroup.Item>
        </ListGroup>
      </Card>
    </Col>
  </Row>
);
 
};

export default TransferEditScreen;
